/** Theme (light / dark / system) and language (EN / VI) segmented switchers — used in the topbar and Settings */

import { Monitor, Moon, Sun, type LucideIcon } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'
import { THEME_PREFERENCES, type ThemePreference } from '../../lib/theme'
import { LOCALES, useT, type Locale, type TranslationKey } from '../../i18n'

const THEME_OPTIONS: Record<ThemePreference, { icon: LucideIcon; labelKey: TranslationKey }> = {
  light: { icon: Sun, labelKey: 'theme.light' },
  dark: { icon: Moon, labelKey: 'theme.dark' },
  system: { icon: Monitor, labelKey: 'theme.system' },
}

const LOCALE_LABEL_KEYS: Record<Locale, TranslationKey> = { en: 'language.en', vi: 'language.vi' }

interface SwitcherProps {
  /** compact: icon/short-code only (topbar); full: visible labels (Settings) */
  variant?: 'compact' | 'full'
  className?: string
}

interface SegmentProps {
  selected: boolean
  onSelect: () => void
  label: string
  children: React.ReactNode
}

function Segment({ selected, onSelect, label, children }: SegmentProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      title={label}
      onClick={onSelect}
      className={cn(
        'flex min-h-8 min-w-8 cursor-pointer items-center justify-center gap-1.5 rounded-md px-2 text-xs font-medium',
        selected ? 'bg-surface-raised text-fg shadow-sm' : 'text-fg-subtle hover:text-fg',
      )}
    >
      {children}
    </button>
  )
}

function SegmentGroup({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn('inline-flex items-center gap-0.5 rounded-lg border border-border bg-surface-muted p-0.5', className)}
    >
      {children}
    </div>
  )
}

export function ThemeSwitcher({ variant = 'compact', className }: SwitcherProps) {
  const t = useT()
  const preference = useAppStore((s) => s.themePreference)
  const setPreference = useAppStore((s) => s.setThemePreference)

  return (
    <SegmentGroup label={t('theme.label')} className={className}>
      {THEME_PREFERENCES.map((value) => {
        const { icon: Icon, labelKey } = THEME_OPTIONS[value]
        return (
          <Segment key={value} selected={preference === value} onSelect={() => setPreference(value)} label={t(labelKey)}>
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            {variant === 'full' && <span>{t(labelKey)}</span>}
          </Segment>
        )
      })}
    </SegmentGroup>
  )
}

export function LanguageSwitcher({ variant = 'compact', className }: SwitcherProps) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const setLocale = useAppStore((s) => s.setLocale)

  return (
    <SegmentGroup label={t('language.label')} className={className}>
      {LOCALES.map((value) => (
        <Segment key={value} selected={locale === value} onSelect={() => setLocale(value)} label={t(LOCALE_LABEL_KEYS[value])}>
          <span lang={value}>{variant === 'full' ? t(LOCALE_LABEL_KEYS[value]) : value.toUpperCase()}</span>
        </Segment>
      ))}
    </SegmentGroup>
  )
}
